import { faBagShopping, faCheckCircle, faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Product } from '@shared/components/ui/Product';
import { Alert, AlertDescription } from '@shared/components/ui/shadcn/alert';
import { formatCurrencyAmount } from '@modules/Orders/shared/hooks/useCurrency';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ProductLine {
  name: string;
  image: string;
  badge: string;
  qty: number;
  price: number;
  subtotal: number;
}

export interface ProductsModalProps {
  orderNumber?: string;
  stockMessage?: string;
  products?: ProductLine[];
  subtotal?: number;
  shipping?: number;
  discount?: number;
  total?: number;
  currency?: string; // Código de moneda (ej: 'EUR', 'MXN', 'USD')
  showNotAssignedAlert?: boolean;
}

export interface ProductsModalRef {
  open: () => void;
  close: () => void;
  openWithData: (data: ProductsModalProps) => void;
}

export const ProductsModal = forwardRef<ProductsModalRef, ProductsModalProps>((props, ref) => {
  const modalRef = useRef<ModalCenterRef>(null);
  const { t } = useTranslation('ordersModule');

  // Estado interno para desacoplar datos de renderizados del padre.
  // Permite abrir la modal con datos sin provocar re-render global.
  const [data, setData] = useState<ProductsModalProps | null>(null);

  // Resolver fuente de datos (estado interno o props para compatibilidad hacia atrás)
  const d = data ?? props;

  // Memoizar lista renderizada para evitar cálculos en re-renders no relacionados
  const productsList = useMemo(() => d.products ?? [], [d.products]);

  const monetary = useMemo(
    () => ({
      subtotal: typeof d.subtotal === 'string' ? parseFloat(d.subtotal) : (d.subtotal ?? 0),
      shipping: typeof d.shipping === 'string' ? parseFloat(d.shipping) : (d.shipping ?? 0),
      discount: typeof d.discount === 'string' ? parseFloat(d.discount) : (d.discount ?? 0),
      total: typeof d.total === 'string' ? parseFloat(d.total) : (d.total ?? 0),
    }),
    [d.subtotal, d.shipping, d.discount, d.total],
  );

  useImperativeHandle(ref, () => ({
    open: () => {
      modalRef.current?.openModal();
    },
    close: () => {
      modalRef.current?.closeModal();
    },
    openWithData: (payload: ProductsModalProps) => {
      setData(payload);
      modalRef.current?.openModal();
    },
  }));

  return (
    <ModalCenter
      ref={modalRef}
      title={
        <span className="flex items-center gap-2">
          <FontAwesomeIcon icon={faBagShopping} className="w-4 h-4" />
          {d.orderNumber}
        </span>
      }
      type="default"
      width="lg"
      className="w-full h-full sm:w-lg sm:h-auto sm:max-w-[579px]"
    >
      {d.showNotAssignedAlert ? (
        // Alerta de stock no asignado (roja)
        <Alert className="border-error dark:border-[#4C0707] mb-6 bg-bg-error dark:bg-[#280B13]">
          <div className="flex items-center gap-3">
            <div>
              <div className="w-8 h-8 bg-[#991B1B] dark:bg-[#4C0707] flex items-center justify-center rounded-full">
                <FontAwesomeIcon icon={faTriangleExclamation} className="text-error light:text-white w-3 h-3" />
              </div>
            </div>
            <div>
              <div className="font-semibold text-error light:text-[#991B1B]">{t('alerts.stockNotAssigned')}</div>
              <AlertDescription className="text-sm text-error light:text-[#991B1B]">
                {t('alerts.stockNotAssignedDescription')}
              </AlertDescription>
            </div>
          </div>
        </Alert>
      ) : (
        // Alerta de stock asignado (verde)
        <Alert className="border-success dark:border-[#064C07] mb-6">
          <div className="flex items-center gap-3">
            <div>
              <div className="w-8 h-8 bg-success dark:bg-[#064C07] flex items-center justify-center rounded-full">
                <FontAwesomeIcon icon={faCheckCircle} className="text-success text-white w-3.5 h-3.5" />
              </div>
            </div>
            <div>
              <div className="font-semibold text-success">{t('alerts.stockAssigned')}</div>
              <AlertDescription className="text-sm text-success">
                {t('alerts.stockAssignedDescription')}
              </AlertDescription>
            </div>
          </div>
        </Alert>
      )}

      {/* Lista de productos y resumen */}
      <div className="space-y-4 max-h-[60vh] overflow-y-auto">
        {productsList.map((p, i) => (
          <div key={i} className="flex items-center justify-between gap-2 border-b border-card-border pb-4">
            <Product
              id={i}
              src={p.image}
              name={p.name}
              code={p.badge}
              acronym={p.name?.[0] || '?'}
              className="min-w-0"
              enableLineClamp={false}
            />
            <div className="flex flex-col items-end">
              <div className="text-sm text-complementary-text">
                {p.qty} x {formatCurrencyAmount(Number(p.price || 0), d.currency || 'EUR')}
              </div>
              <div className="font-sm">{formatCurrencyAmount(Number(p.subtotal || 0), d.currency || 'EUR')}</div>
            </div>
          </div>
        ))}

        {/* Resumen dentro del scroll */}
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-complementary-text">Subtotal</span>
            <span>{formatCurrencyAmount(monetary.subtotal, d.currency || 'EUR')}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-complementary-text">Gastos de envío</span>
            <span>{formatCurrencyAmount(monetary.shipping, d.currency || 'EUR')}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-complementary-text">Descuentos</span>
            <span>{formatCurrencyAmount(monetary.discount, d.currency || 'EUR')}</span>
          </div>
          <div className="flex justify-between font-bold text-lg mt-2">
            <span>Total</span>
            <span>{formatCurrencyAmount(monetary.total, d.currency || 'EUR')}</span>
          </div>
        </div>
      </div>
    </ModalCenter>
  );
});

ProductsModal.displayName = 'ProductsModal';
